import blogIndex from './blog/index.json';
import recipesIndex from './recipes/index.json';
import seasonalIndex from './seasonal/index.json';
import produceIndex from './produce/index.json';

export const blogPosts = Array.isArray(blogIndex?.posts) ? blogIndex.posts : [];
export const recipes = Array.isArray(recipesIndex?.recipes) ? recipesIndex.recipes : [];
export const seasonalGuides = Array.isArray(seasonalIndex?.guides) ? seasonalIndex.guides : [];
export const produceProducts = Array.isArray(produceIndex?.products) ? produceIndex.products : [];

export const getBlogPostBySlug = (slug) => {
  if (!slug) return undefined;
  return blogPosts.find((post) => post.slug === slug);
};

export const getFeaturedBlogPosts = (limit = null) => {
  const featured = blogPosts.filter((post) => post.featured);
  return limit ? featured.slice(0, limit) : featured;
};

export const getBlogCategories = () => {
  const counts = blogPosts.reduce((acc, post) => {
    const key = post.category || 'uncategorized';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));
};

export const getBlogTags = (limit = 20) => {
  const counts = blogPosts.reduce((acc, post) => {
    for (const tag of post.tags || []) {
      acc[tag] = (acc[tag] || 0) + 1;
    }
    return acc;
  }, {});
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([tag]) => tag);
};

export const getRecipeBySlug = (slug) => {
  if (!slug) return undefined;
  return recipes.find((recipe) => recipe.slug === slug || recipe.id === slug);
};

export const getRecipeById = (id) => {
  if (!id) return undefined;
  return recipes.find((recipe) => recipe.id === id);
};

export const getSeasonalGuideById = (id) => {
  if (!id) return undefined;
  return seasonalGuides.find((guide) => guide.id === id);
};

export const getProduceProductById = (id) => {
  if (!id) return undefined;
  return produceProducts.find((product) => product.id === id);
};

// ============= NEW FILTER FUNCTIONS FOR BIDIRECTIONAL LINKING =============

/**
 * Get recipes by dietary restriction
 * KEY for Recipes menu filtering
 */
export const getRecipesByDiet = (diet, limit = null) => {
  const filtered = recipes.filter(recipe =>
    recipe.dietaryTags && recipe.dietaryTags.some(tag =>
      tag.toLowerCase().includes(diet.toLowerCase())
    )
  );
  return limit ? filtered.slice(0, limit) : filtered;
};

/**
 * Get recipes by difficulty level
 * KEY for Recipes menu filtering
 */
export const getRecipesByDifficulty = (difficulty, limit = null) => {
  const filtered = recipes.filter(recipe =>
    recipe.difficulty && recipe.difficulty.toLowerCase() === difficulty.toLowerCase()
  );
  return limit ? filtered.slice(0, limit) : filtered;
};

/**
 * Get quick recipes (30 minutes or less)
 * KEY for Recipes menu filtering
 */
export const getQuickRecipes = (maxTime = 30, limit = null) => {
  const filtered = recipes.filter(recipe => {
    const prepTime = typeof recipe.prepTime === 'number' ? recipe.prepTime :
                     parseInt(String(recipe.prepTime).match(/\d+/)?.[0] || '0');
    const cookTime = typeof recipe.cookTime === 'number' ? recipe.cookTime :
                     parseInt(String(recipe.cookTime).match(/\d+/)?.[0] || '0');
    return (prepTime + cookTime) <= maxTime;
  });
  return limit ? filtered.slice(0, limit) : filtered;
};

/**
 * Get recipes using a specific ingredient/produce
 * 🌟 CRITICAL for "Basil Example" - Basil page → Recipes using basil
 */
export const getRecipesByIngredient = (ingredient, limit = null) => {
  const ingredientLower = ingredient.toLowerCase();
  const filtered = recipes.filter(recipe => {
    if (!recipe.primaryProduce) return false;
    return recipe.primaryProduce.some(produce =>
      produce.toLowerCase().includes(ingredientLower) ||
      ingredientLower.includes(produce.toLowerCase())
    );
  });
  return limit ? filtered.slice(0, limit) : filtered;
};

/**
 * Get recipe categories with counts
 */
export const getRecipeCategories = () => {
  const counts = recipes.reduce((acc, recipe) => {
    const cuisine = recipe.cuisine || 'Other';
    acc[cuisine] = (acc[cuisine] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Get produce by category (fruit, vegetable, herb)
 * KEY for Produce menu filtering
 */
export const getProduceByCategory = (category, limit = null) => {
  // Category might be in the id or a separate field
  const categoryLower = category.toLowerCase();
  const filtered = produceProducts.filter(product => {
    const productCategory = product.category || product.type || '';
    return productCategory.toLowerCase().includes(categoryLower);
  });
  return limit ? filtered.slice(0, limit) : filtered;
};

/**
 * Get produce by season/month
 */
export const getProduceBySeason = (month, limit = null) => {
  const filtered = produceProducts.filter(product => {
    if (!product.season) return false;
    // Season could be array of months or string
    if (Array.isArray(product.season)) {
      return product.season.includes(month);
    }
    return true; // Include if season info exists but format unknown
  });
  return limit ? filtered.slice(0, limit) : filtered;
};

/**
 * Get similar produce items
 */
export const getSimilarProduce = (productId, limit = 3) => {
  const currentProduct = getProduceProductById(productId);
  if (!currentProduct) return [];

  const category = currentProduct.category || currentProduct.type || '';
  return produceProducts
    .filter(p =>
      p.id !== productId &&
      (p.category === category || p.type === category)
    )
    .slice(0, limit);
};

/**
 * Get seasonal guide by month number
 */
export const getSeasonalGuideByMonth = (month) => {
  return seasonalGuides.find(guide => guide.month === month);
};

/**
 * Get all seasonal guides sorted by month
 */
export const getAllSeasonalGuides = () => {
  return [...seasonalGuides].sort((a, b) => (a.month || 0) - (b.month || 0));
};

/**
 * Get related blog posts by category/tags
 */
export const getRelatedBlogPosts = (postId, limit = 3) => {
  const currentPost = getBlogPostBySlug(postId) || blogPosts.find(p => p.id === postId);
  if (!currentPost) return [];

  const category = currentPost.category;
  const tags = currentPost.tags || [];

  const scored = blogPosts
    .filter(post => post.id !== postId && post.slug !== postId)
    .map(post => {
      let score = 0;
      if (post.category === category) score += 3;
      if (post.tags) {
        const sharedTags = post.tags.filter(tag => tags.includes(tag)).length;
        score += sharedTags;
      }
      return { post, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map(item => item.post);
};

/**
 * Get blog posts by category
 */
export const getBlogPostsByCategory = (category, limit = null) => {
  const filtered = blogPosts.filter(post =>
    post.category && post.category.toLowerCase() === category.toLowerCase()
  );
  return limit ? filtered.slice(0, limit) : filtered;
};

